import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { apiClient } from '../api/client';
import type { SearchKind, SearchPage } from '../api/searchTypes';

// WS-4a (C2) — React Query binding for `/api/v1/search`. The
// caller controls the debounce; this hook just fetches once `enabled`
// flips true.

export const searchKeys = {
  query: (params: { q: string; types: SearchKind[]; project: string | null; page: number }) =>
    ['search', params] as const,
};

export interface GlobalSearchInput {
  q: string;
  types: SearchKind[];
  projectId?: string;
  page?: number;
  limit?: number;
  enabled?: boolean;
}

export function useGlobalSearch(input: GlobalSearchInput) {
  const q = input.q.trim();
  const page = input.page ?? 1;
  const limit = input.limit ?? 20;
  return useQuery({
    queryKey: searchKeys.query({
      q,
      types: input.types,
      project: input.projectId ?? null,
      page,
    }),
    queryFn: async (): Promise<SearchPage> => {
      const params = new URLSearchParams();
      params.set('q', q);
      if (input.types.length > 0) params.set('types', input.types.join(','));
      if (input.projectId) params.set('project', input.projectId);
      params.set('page', String(page));
      params.set('limit', String(limit));
      const res = await apiClient.get<SearchPage>(
        `/search?${params.toString()}`,
      );
      return res.data;
    },
    enabled: (input.enabled ?? true) && q.length > 0,
    placeholderData: keepPreviousData,
    staleTime: 30 * 1000,
  });
}
